import type { TextFieldProps } from '@mui/material/TextField'
import type { SxProps, Theme } from '@mui/material/styles'

export const MICHIGAN_TITLES_DOCUMENT_DOWNLOAD_LABEL = 'DOWNLOAD'

const michiganTitlesTableBodyFontSize = '9pt'
const michiganTitlesTableHeaderFontSize = '0.8rem'
const michiganTitlesControlMinHeight = 28

/** Plain style objects for TextField/Button `sx` (avoids SxProps array typing issues). */
export const michiganTitlesCompactTextFieldSx = {
  '& .MuiInputBase-root': {
    fontSize: michiganTitlesTableBodyFontSize
  },
  '& .MuiOutlinedInput-root': {
    minHeight: michiganTitlesControlMinHeight
  },
  '& .MuiOutlinedInput-input': {
    fontSize: michiganTitlesTableBodyFontSize,
    py: 0.375,
    px: 0.75
  },
  '& .MuiSelect-select': {
    fontSize: michiganTitlesTableBodyFontSize,
    py: 0.375,
    minHeight: 'unset !important'
  },
  '& .MuiInputLabel-root': {
    fontSize: michiganTitlesTableHeaderFontSize
  },
  '& .MuiFormHelperText-root': {
    fontSize: '0.7rem',
    lineHeight: 1.2,
    m: 0,
    mt: 0.25
  }
}

export const michiganTitlesNextPoliceFieldSx = {
  ...michiganTitlesCompactTextFieldSx,
  width: 120,
  maxWidth: 120,
  flexShrink: 0,
  '& .MuiOutlinedInput-root': {
    minHeight: michiganTitlesControlMinHeight,
    height: michiganTitlesControlMinHeight
  },
  '& .MuiOutlinedInput-input': {
    fontSize: michiganTitlesTableBodyFontSize,
    py: 0,
    px: 0.75,
    height: '100%',
    boxSizing: 'border-box'
  }
}

export const michiganTitlesPoliceBookNumberFieldSx = {
  ...michiganTitlesCompactTextFieldSx,
  maxWidth: 96
}

export const michiganTitlesCompactButtonSx = {
  fontSize: michiganTitlesTableHeaderFontSize,
  lineHeight: 1.25,
  py: 0.375,
  px: 1.25,
  minHeight: michiganTitlesControlMinHeight,
  textTransform: 'none'
}

export const michiganTitlesPoliceBookToolbarSx: SxProps<Theme> = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 1
}

/** Address/party table: middle-align all rows (text-only and select rows). */
export const michiganTitlesPartyTableSx: SxProps<Theme> = {
  '& .MuiTableCell-body': {
    verticalAlign: 'middle'
  }
}

export const michiganTitlesPartyActionsCellSx: SxProps<Theme> = {
  verticalAlign: 'middle',
  whiteSpace: 'nowrap'
}

export const michiganTitlesCompactFormControlLabelSx = {
  m: 0,
  alignItems: 'center',
  gap: 0.5,
  '& .MuiFormControlLabel-label': {
    fontSize: michiganTitlesTableBodyFontSize,
    lineHeight: 1.35
  },
  '& .MuiCheckbox-root': {
    padding: '2px'
  }
}

export const michiganTitlesCompactSelectProps: TextFieldProps['SelectProps'] = {
  MenuProps: {
    PaperProps: {
      sx: {
        '& .MuiMenuItem-root': {
          fontSize: michiganTitlesTableBodyFontSize,
          py: 0.5,
          minHeight: michiganTitlesControlMinHeight
        }
      }
    }
  }
}

export const michiganTitlesTableContainerSx: SxProps<Theme> = {
  overflowX: 'auto'
}

export const michiganTitlesTableSx: SxProps<Theme> = theme => ({
  tableLayout: 'auto',
  '& .MuiTableCell-head': {
    color: theme.palette.primary.main,
    fontSize: michiganTitlesTableHeaderFontSize,
    fontWeight: 600,
    lineHeight: 1.25,
    py: 0.75,
    px: 0.75,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    verticalAlign: 'bottom'
  },
  '& .MuiTableSortLabel-root': {
    fontSize: 'inherit',
    fontWeight: 'inherit',
    lineHeight: 'inherit',
    alignItems: 'flex-start',
    color: 'inherit'
  },
  '& .MuiTableSortLabel-icon': {
    fontSize: '1rem'
  },
  '& .MuiTableCell-body': {
    fontSize: michiganTitlesTableBodyFontSize,
    lineHeight: 1.35,
    py: 0.5,
    px: 0.75,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    verticalAlign: 'top'
  },
  '& .MuiTableRow-root:has(.MuiFormControl-root) .MuiTableCell-body': {
    verticalAlign: 'middle'
  },
  '& .MuiTableCell-body .MuiLink-root': {
    fontSize: 'inherit',
    lineHeight: 'inherit',
    whiteSpace: 'nowrap'
  },
  '& .MuiTableCell-body .MuiButton-root': {
    fontSize: michiganTitlesTableHeaderFontSize,
    lineHeight: 1.25,
    minWidth: 0,
    py: 0.375,
    px: 0.75,
    minHeight: michiganTitlesControlMinHeight
  },
  '& .MuiTableCell-body .MuiFormControl-root': {
    my: 0,
    justifyContent: 'center'
  },
  '& .MuiTableCell-body .MuiOutlinedInput-root': {
    minHeight: michiganTitlesControlMinHeight
  },
  '& .MuiTableCell-body .MuiInputBase-input': {
    fontSize: michiganTitlesTableBodyFontSize,
    py: 0.375,
    px: 0.75
  },
  '& .MuiTableCell-body .MuiSelect-select': {
    fontSize: michiganTitlesTableBodyFontSize,
    py: 0.375,
    minHeight: 'unset !important'
  },
  '& .MuiTableCell-body .MuiFormHelperText-root': {
    fontSize: '0.7rem',
    lineHeight: 1.2,
    m: 0,
    mt: 0.25
  },
  '& .MuiTableCell-paddingCheckbox': {
    py: 0.25,
    px: 0.5,
    width: 40,
    verticalAlign: 'middle'
  },
  '& .MuiTablePagination-root': {
    fontSize: michiganTitlesTableBodyFontSize
  },
  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select': {
    fontSize: michiganTitlesTableBodyFontSize
  },
  '& .MuiTablePagination-toolbar': {
    minHeight: 40
  }
})

/** Base table styles plus party-table body middle alignment (single `sx` for MUI typing). */
export const michiganTitlesPartyTableMergedSx: SxProps<Theme> = theme => {
  const tableStyles = michiganTitlesTableSx(theme) as Record<string, Record<string, unknown> | unknown>
  const bodyCellStyles = tableStyles['& .MuiTableCell-body'] as Record<string, unknown> | undefined

  return {
    ...tableStyles,
    '& .MuiTableCell-body': {
      ...bodyCellStyles,
      verticalAlign: 'middle'
    }
  }
}

export const michiganTitlesDateCellSx: SxProps<Theme> = {
  whiteSpace: 'nowrap'
}

export const michiganTitlesCompactCellSx: SxProps<Theme> = {
  whiteSpace: 'nowrap'
}

export const michiganTitlesDocumentCellSx: SxProps<Theme> = {
  whiteSpace: 'nowrap'
}

export const michiganTitlesMissingCellSx: SxProps<Theme> = theme => ({
  color: theme.palette.error.main
})

export const michiganTitlesRowLabelCellSx: SxProps<Theme> = theme => ({
  color: theme.palette.primary.main,
  fontWeight: 600,
  verticalAlign: 'middle'
})

export const michiganTitlesLinkSx: SxProps<Theme> = {
  color: 'inherit',
  cursor: 'pointer',
  fontSize: 'inherit',
  lineHeight: 'inherit',
  whiteSpace: 'nowrap'
}
